import { Text, OrderedList, ListItem, Stack } from "@chakra-ui/react";
import { useTextVariants } from "@/theme/components/useSectionHeading";
import KubernetesImage from "public/iaas/kubernetes.png";

const TextComponent = ({
  ans,
  subList,
}: {
  ans: string;
  subList: { label: string; text: string }[];
}) => {
  const textVariant = useTextVariants();
  return (
    <Stack spacing={2}>
      <Text variant={textVariant} color="text.100">
        {ans}
      </Text>
      <OrderedList spacing={1} color="text.100" listStyleType="lower-alpha">
        {subList.map((point, index) => (
          <ListItem key={index}>
            <Text as="span" variant={textVariant} key={index} fontWeight={700}>
              {point.label} - {""}
            </Text>
            <Text as="span" variant={textVariant} key={index} color="text.100">
              {point.text}
            </Text>
          </ListItem>
        ))}
      </OrderedList>
    </Stack>
  );
};

export const IMPLEMENTATION_CONSTANTS = {
  image: KubernetesImage,
  title: "Implementing Private Kubernetes Clusters",
  desc: "To deploy a private Kubernetes cluster, consider the following steps:",
  steps: [
    {
      label: "Cluster Deployment",
      text: "Utilize tools like kubeadm or managed Kubernetes services that support private cluster configurations.",
    },
    {
      label: "Networking Configuration",
      text: "Set up network policies to control traffic flow between pods and services, ensuring that only authorized communications occur.",
    },
    {
      label: "Access Control",
      text: "Implement VPNs or bastion hosts to manage secure access to the cluster's API server.",
    },
    {
      label: "Service Exposure",
      text: (
        <TextComponent
          ans="For external access to applications, configure services appropriately. Kubernetes supports three service types to establish internal and external connections: "
          subList={[
            {
              label: "ClusterIP",
              text: "Exposes the service on an internal IP within the cluster, making it accessible only within the cluster. ",
            },
            {
              label: "NodePort",
              text: "Exposes the service on a static port on each node's IP, allowing external traffic to access the service. ",
            },
            {
              label: "LoadBalancer",
              text: "Provisions an external load balancer, making the service accessible from outside the cluster. ",
            },
          ]}
        />
      ),
    },
    {
      label: "Persistent Storage",
      text: "Implement Persistent Volumes (PV) and Persistent Volume Claims (PVC) for data retention across pod restarts. Kubernetes supports automatic configuration of NFS volume provisioners.",
    },
    {
      label: "DNS Configuration",
      text: "Ensure proper DNS setup for internal service discovery using tools like CoreDNS for efficient internal DNS resolution.",
    },
    {
      label: "Monitoring and Logging",
      text: "Integrate solutions like Prometheus for monitoring and the ELK Stack for logging to track cluster performance and security events.",
    },
    {
      label: "Regular Updates and Patching",
      text: "Apply regular updates and patches to Kubernetes components and underlying infrastructure to protect against vulnerabilities.",
    },
  ],
};
